import { isEnterpriseAdmin } from '../enterprise';
import { isPremOrganizationAdmin } from '../organization';
import { canInviteMembers } from './canInviteMembers';
import { compareMemberTypes } from './compareMemberTypes';
import { getMemberTypeFromBoard } from './getMemberTypeFromBoard';
import { isAdminOfBoard } from './isAdminOfBoard';
import { isImplicitBoardAdmin } from './isImplicitBoardAdmin';

/**
 * Checks if a member has permissions to remove members from the board.
 */
export const canRemoveMember = (
  me: {
    id: string;
    idPremOrgsAdmin: Array<string>;
    memberType: 'ghost' | 'normal';
  } & {
    idEnterprisesAdmin?: string[] | null;
  },
  subject: {
    id: string;
    idPremOrgsAdmin: Array<string>;
    memberType: 'ghost' | 'normal';
  } & {
    idEnterprisesAdmin?: string[] | null;
  },
  board: {
    memberships: Array<{
      __typename: 'Board_Membership';
      deactivated: boolean;
      id: string;
      idMember: string;
      memberType: 'admin' | 'normal' | 'observer';
      orgMemberType: 'admin' | 'normal';
      unconfirmed: boolean;
    }>;
  } & {
    prefs?: { canInvite: boolean; invitations: 'admins' | 'members' } | null;
    idOrganization?: string | null;
    idEnterprise?: string | null;
    members: ({ id: string; idPremOrgsAdmin: Array<string> } & {
      idEnterprisesAdmin?: string[] | null;
    })[];
  },
  organization?:
    | ({ id: string; offering: string } & {
        memberships: {
          id: string;
          deactivated: boolean;
          idMember: string;
          memberType: 'admin' | 'normal';
          unconfirmed: boolean;
        }[];
      })
    | null,
): boolean => {
  // There are cases where `memberships` are returning null
  // and are not caught by typescript
  // TODO investigate how board.memberships can be null and why
  // typescript is not catching these errors
  if (!board.memberships) {
    return false;
  }

  // If there are no other explicit board admins, also check if this board is in a paid organization and has a member who is a organization admin
  const boardHasMoreThanOneAdmin = board.memberships.some(
    ({
      idMember,
      memberType,
    }: {
      idMember: string;
      memberType: 'admin' | 'normal' | 'observer';
    }) => idMember !== subject.id && memberType === 'admin',
  );

  // Only checks board memberships, so will return false if member is not part of the board
  const isBoardMemberAndImplicitAdmin = isImplicitBoardAdmin(board, me.id);

  const isPremWorkspaceAdmin = isPremOrganizationAdmin(
    me,
    board.idOrganization ?? '',
  );
  const isEntAdmin = isEnterpriseAdmin(
    { idEnterprisesAdmin: me.idEnterprisesAdmin ?? [] },
    board.idEnterprise ?? '',
  );
  const isImplicitAdmin =
    isBoardMemberAndImplicitAdmin || isPremWorkspaceAdmin || isEntAdmin;

  const boardHasMoreThanOneMember = board.memberships.length > 1;
  const amAdmin = isAdminOfBoard(me, board);
  const amSubject = me.id === subject.id;

  if (boardHasMoreThanOneMember) {
    if (boardHasMoreThanOneAdmin) {
      if (amAdmin || amSubject || isImplicitAdmin) {
        return true;
      }
    } else if (!isAdminOfBoard(subject, board, false)) {
      if (isImplicitAdmin) {
        return true;
      }
    } else if (isAdminOfBoard(subject, board, false)) {
      // The last board admin may never be removed
      return false;
    }

    // on a given member in board.members yet
    const subjectMemberType = getMemberTypeFromBoard(
      subject,
      board,
      organization,
      false,
    );
    if (
      !amSubject &&
      subjectMemberType !== 'org' &&
      canInviteMembers(me, board) &&
      compareMemberTypes(
        getMemberTypeFromBoard(me, board, organization),
        subjectMemberType,
      ) >= 0
    ) {
      return true;
    }
  }
  return false;
};
